import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'apob', 'ldl', 'totalChol', 'hdl', 'triglycerides',
    'apobDisplay', 'apobStatus', 'apobInterpretation',
    'ldlDisplay', 'ldlStatus', 'ldlInterpretation',
    'tcHdlRatio', 'tgHdlRatio', 'keyInsight'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const apob = parseFloat(this.apobTarget.value) || 0
    const ldl = parseFloat(this.ldlTarget.value) || 0
    const totalChol = parseFloat(this.totalCholTarget.value) || 0
    const hdl = parseFloat(this.hdlTarget.value) || 0
    const tg = parseFloat(this.triglyceridesTarget.value) || 0

    // ApoB interpretation
    if (apob > 0) {
      this.apobDisplayTarget.textContent = apob
      const apobResult = this.interpretApoB(apob)
      this.apobStatusTarget.textContent = apobResult.status
      this.apobStatusTarget.className = `px-3 py-1 text-sm font-medium ${apobResult.bgClass}`
      this.apobInterpretationTarget.textContent = apobResult.interpretation
    }

    // LDL interpretation
    if (ldl > 0) {
      this.ldlDisplayTarget.textContent = ldl
      const ldlResult = this.interpretLDL(ldl)
      this.ldlStatusTarget.textContent = ldlResult.status
      this.ldlStatusTarget.className = `px-3 py-1 text-sm font-medium ${ldlResult.bgClass}`
      this.ldlInterpretationTarget.textContent = ldlResult.interpretation
    }

    // Ratios
    if (totalChol > 0 && hdl > 0) {
      const tcHdl = (totalChol / hdl).toFixed(1)
      this.tcHdlRatioTarget.textContent = tcHdl
    } else {
      this.tcHdlRatioTarget.textContent = '--'
    }

    if (tg > 0 && hdl > 0) {
      const tgHdl = (tg / hdl).toFixed(1)
      this.tgHdlRatioTarget.textContent = tgHdl
    } else {
      this.tgHdlRatioTarget.textContent = '--'
    }

    // Key insight
    this.updateKeyInsight(apob, ldl)
  }

  interpretApoB(value) {
    if (value < 70) {
      return {
        status: 'OPTIMAL',
        bgClass: 'bg-green-100 text-green-800',
        interpretation: 'Excellent. This level is associated with very low cardiovascular risk. ' +
          'Recommended for those with existing CVD or very high risk.'
      }
    }
    if (value < 90) {
      return {
        status: 'GOOD',
        bgClass: 'bg-green-50 text-green-700',
        interpretation: 'Good level. This is considered optimal for most people. Associated with low cardiovascular risk.'
      }
    }
    if (value < 130) {
      return {
        status: 'BORDERLINE',
        bgClass: 'bg-yellow-100 text-yellow-800',
        interpretation: 'Within standard reference range but above optimal. Consider lifestyle modifications if other risk factors are present.'
      }
    }
    return {
      status: 'ELEVATED',
      bgClass: 'bg-red-100 text-red-800',
      interpretation: 'Elevated ApoB indicates a higher number of atherogenic particles. Discuss with your healthcare provider.'
    }
  }

  interpretLDL(value) {
    if (value < 70) {
      return {
        status: 'OPTIMAL',
        bgClass: 'bg-green-100 text-green-800',
        interpretation: 'Excellent. This level is recommended for high-risk individuals with existing cardiovascular disease.'
      }
    }
    if (value < 100) {
      return {
        status: 'NEAR OPTIMAL',
        bgClass: 'bg-green-50 text-green-700',
        interpretation: 'Near optimal level. Good for most people without significant cardiovascular risk factors.'
      }
    }
    if (value < 130) {
      return {
        status: 'BORDERLINE HIGH',
        bgClass: 'bg-yellow-100 text-yellow-800',
        interpretation: 'Borderline high. Lifestyle modifications recommended. Medication may be considered based on overall risk.'
      }
    }
    if (value < 160) {
      return {
        status: 'HIGH',
        bgClass: 'bg-orange-100 text-orange-800',
        interpretation: 'High LDL cholesterol. Discuss treatment options with your healthcare provider.'
      }
    }
    return {
      status: 'VERY HIGH',
      bgClass: 'bg-red-100 text-red-800',
      interpretation: 'Very high LDL. Treatment is typically recommended. Consult your healthcare provider.'
    }
  }

  updateKeyInsight(apob, ldl) {
    let insight = 'ApoB is often considered a more accurate predictor of cardiovascular risk than LDL-C ' +
      'because it counts atherogenic particles directly.'

    if (apob > 0 && ldl > 0) {
      // Check for discordance
      const apobOptimal = apob < 90
      const ldlOptimal = ldl < 100

      if (apobOptimal && !ldlOptimal) {
        insight = 'Your ApoB is better than LDL-C suggests. This discordance means you may have fewer, ' +
          'larger LDL particles. Risk may be lower than LDL-C alone indicates.'
      } else if (!apobOptimal && ldlOptimal) {
        insight = 'Your ApoB is higher than LDL-C suggests. This discordance indicates more numerous, ' +
          'smaller LDL particles. Risk may be higher than LDL-C alone indicates.'
      }
    }

    this.keyInsightTarget.textContent = insight
  }
}
